import {
  BookingModuleStoreType,
  CalendarRootStoreSlice,
  CalendarRootStoreSliceActions,
  ProjectsActions,
  ProjectsCounters,
  ProjectsSlice
} from "PFApp/booking/store";
import {
  calculateAllBookingsModeState,
  checkIsAllBookingsModeApplicable
} from "PFApp/booking/store/provider/calendar_root_rows/calculate_all_bookings_mode_state";
import { resetRowChildrenPagination } from "PFApp/booking/store/provider/get_calendar_root_store_slice_providers.utils";
import {
  expandRowAndItsChildren,
  markRowAndChildrenByEnabledFlag,
  resetRowAndChildrenAllBookingsModeState
} from "PFApp/booking/store/provider/use_projects_store_slice.utils";
import { ProjectsView, Row } from "PFApp/booking/types";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { CalendarGranularity } from "PFCore/hooks/queries/profile/use_profiles_availabilities";
import { StateCreator } from "zustand";

import { getCalendarRootStoreSliceProviders } from "./get_calendar_root_store_slice_providers";

type ProjectsSliceCreator = StateCreator<
  BookingModuleStoreType,
  [],
  [],
  CalendarRootStoreSlice<ProjectsSlice, ProjectsCounters>
>;
type ProjectsSliceActionsCreator = StateCreator<
  BookingModuleStoreType,
  [],
  [],
  CalendarRootStoreSliceActions<ProjectsCounters, ProjectsActions>
>;

type UseProjectsStoreState<T extends CalendarRoot.ProjectsRoles | CalendarRoot.ProjectsEngagements> = {
  calendarRoot: T;
};
type UseProjectsStoreStateReturn = {
  createProjectsStoreSlice: ProjectsSliceCreator;
  createProjectsStoreSliceActions: ProjectsSliceActionsCreator;
};

export const useProjectsStoreSlice = <
  T extends CalendarRoot.ProjectsRoles | CalendarRoot.ProjectsEngagements
>({
  calendarRoot
}: UseProjectsStoreState<T>): UseProjectsStoreStateReturn => {
  const { createCalendarRootStoreSlice, createCalendarRootStoreSliceActions } =
    getCalendarRootStoreSliceProviders({ calendarRoot });

  const createProjectsStoreSlice: ProjectsSliceCreator = () => ({
    view: ProjectsView.Gantt,
    calendarGranularity: CalendarGranularity.Daily,
    allBookingsModeRowsState: {},
    warningsByActivity: {},
    ...createCalendarRootStoreSlice()
  });

  const createProjectsStoreSliceActions: ProjectsSliceActionsCreator = (...args) => {
    const [set, get] = args;
    return {
      setView: (view) => {
        const projectsSlice = get()[calendarRoot];
        set({ [calendarRoot]: { ...projectsSlice, view } });
      },
      setCalendarGranularity: (calendarGranularity) => {
        const projectsSlice = get()[calendarRoot];
        set({ [calendarRoot]: { ...projectsSlice, calendarGranularity } });
      },
      setAllBookingsModeRowsState: (allBookingsModeRowsState) => {
        const projectsSlice = get()[calendarRoot];
        set({ [calendarRoot]: { ...projectsSlice, allBookingsModeRowsState } });
      },
      setAllBookingsModeForRow: (rowKey, enabled) => {
        const projectsSlice = get()[calendarRoot];

        // mark the row + its applicable children using the `enabled` flag
        // marks the children as `all` in case enabled=true or `unset` in case enabled=false
        const updatedAllBookingsModeState = markRowAndChildrenByEnabledFlag(
          projectsSlice.allBookingsModeRowsState,
          rowKey,
          enabled
        );

        // calculate the final state providing the updated state (to avoid ambiguity)
        const allBookingsModeRowsState = calculateAllBookingsModeState<Row>({
          allBookingsModeState: updatedAllBookingsModeState,
          items: projectsSlice.rows,
          isApplicable: (row) => checkIsAllBookingsModeApplicable(row, calendarRoot)
        });
        if (enabled) {
          // enabling the row should trigger expanding it and its children
          const expandedState = expandRowAndItsChildren(
            {
              ...projectsSlice.expandedRowsState,
              [rowKey]: true
            },
            rowKey
          );
          get().actions[calendarRoot].setExpandedRowsState(expandedState);
        }
        set({ [calendarRoot]: { ...get()[calendarRoot], allBookingsModeRowsState } }); // get()[calendarRoot] instead of projectSlice to get the newest state
      },
      setWarningsByActivity: (warningsByActivity) => {
        const projectsSlice = get()[calendarRoot];
        set({ [calendarRoot]: { ...projectsSlice, warningsByActivity } });
      },
      ...createCalendarRootStoreSliceActions(...args),
      setExpandedForRow: (rowKey, expanded) => {
        const projectsSlice = get()[calendarRoot];

        // when collapsing the row, reset it and its children allBookingsMode state
        const updatedAllBookingsModeState = expanded
          ? projectsSlice.allBookingsModeRowsState
          : resetRowAndChildrenAllBookingsModeState(projectsSlice.allBookingsModeRowsState, rowKey);

        // when collapsing the row, reset it and its children pagination state
        const updatedPaginationByRowKey = expanded
          ? projectsSlice.paginationByRowKey
          : resetRowChildrenPagination(projectsSlice.paginationByRowKey, rowKey);

        set({
          [calendarRoot]: {
            ...projectsSlice,
            expandedRowsState: { ...projectsSlice.expandedRowsState, [rowKey]: expanded },
            allBookingsModeRowsState: updatedAllBookingsModeState,
            paginationByRowKey: updatedPaginationByRowKey
          }
        });
      }
    };
  };

  return { createProjectsStoreSlice, createProjectsStoreSliceActions };
};
